/**
 * useDiagnosticPanel - SM-4 诊断面板状态机
 *
 * Implements the SM-4 diagnostic panel state machine as a standalone hook
 * using `useReducer` (pure reducer) + side-effect effects. The reducer is a
 * pure function exported as {@link diagnosticPanelReducer}; all Web Animations
 * API side effects (`element.animate()` + `onfinish` dispatch) live in
 * {@link useDiagnosticPanel}.
 *
 * 使用 `useReducer`（纯 reducer）+ 副作用 effect 实现 SM-4 诊断面板状态机。
 * reducer 为纯函数 {@link diagnosticPanelReducer}；所有 Web Animations API
 * 副作用（element.animate() + onfinish 派发）位于 {@link useDiagnosticPanel}。
 *
 * States / 状态:
 *   Hidden → Appearing → Visible ⇄ Updating → Disappearing → Hidden
 *
 * Full transition table / 完整状态转移表:
 *   Hidden        + SHOW                     → Appearing (set currentTurnIndex)
 *   Appearing     + APPEAR_ANIMATION_END     → Visible
 *   Visible       + UPDATE_TURN              → Updating (save prevTurnIndex)
 *   Visible       + HIDE                     → Disappearing
 *   Updating      + UPDATE_ANIMATION_END     → Visible
 *   Updating      + HIDE                     → Disappearing
 *   Disappearing  + DISAPPEAR_ANIMATION_END  → Hidden (clear currentTurnIndex)
 *
 * Animations (Web Animations API, element.animate()) / 动画:
 *   Appearing:     opacity 0→1, translateY 8px→0px, 200ms, ease-out
 *   Updating:      cross-fade 300ms — fade out (opacity 1→0, 150ms) →
 *                  content swap → fade in (opacity 0→1, 150ms)
 *   Disappearing:  opacity 1→0, translateY 0px→8px, 200ms, ease-in
 *
 * Updating cross-fade detail / Updating 交叉淡入淡出说明:
 *   The reducer sets the new `currentTurnIndex` immediately on UPDATE_TURN.
 *   The animation fades the panel out (150ms) then back in (150ms); the
 *   opacity dip at the 150ms midpoint visually masks the content swap,
 *   achieving the same effect as "old fades out → swap → new fades in".
 *
 *   reducer 在 UPDATE_TURN 时立即设置新的 currentTurnIndex。动画先将面板
 *   淡出（150ms）再淡入（150ms）；150ms 处的透明度低谷在视觉上遮盖内容切换，
 *   效果等同于"旧内容淡出 → 切换 → 新内容淡入"。
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { RefObject } from 'react';
import { DiagnosticPanelState } from '../types/enums';
import type {
  DiagnosticPanelContext,
  DiagnosticPanelEvent,
} from '../types/state-machine';

// ===== Constants =====

/** Appearing animation duration (ms). / 出现动画时长（毫秒）。 */
const APPEAR_DURATION_MS = 200;

/** Disappearing animation duration (ms). / 消失动画时长（毫秒）。 */
const DISAPPEAR_DURATION_MS = 200;

/** Cross-fade fade-out phase duration (ms). / 淡出阶段时长（毫秒）。 */
const UPDATE_FADE_OUT_MS = 150;

/** Cross-fade fade-in phase duration (ms). / 淡入阶段时长（毫秒）。 */
const UPDATE_FADE_IN_MS = 150;

/** translateY offset for enter/exit animations (px). / 进出动画 translateY 偏移（像素）。 */
const TRANSLATE_OFFSET_PX = 8;

// ===== Initial State =====

/** Initial DiagnosticPanelContext (panel hidden). / 初始上下文（面板隐藏）。 */
const initialState: DiagnosticPanelContext = {
  state: DiagnosticPanelState.Hidden,
  currentTurnIndex: null,
  animationStartTime: null,
  prevTurnIndex: null,
};

// ===== Pure Reducer =====

/**
 * SM-4 diagnostic panel reducer (pure function).
 *
 * `animationStartTime` is set to `Date.now()` when entering an animation
 * state (Appearing / Updating / Disappearing) and cleared (`null`) when the
 * corresponding `*_ANIMATION_END` event arrives. This field is for debugging
 * and is also used as a useEffect dependency to re-trigger animations.
 *
 * 进入动画状态（Appearing / Updating / Disappearing）时 animationStartTime 置为
 * Date.now()；对应 *_ANIMATION_END 事件到达时置 null。此字段用于调试，同时
 * 作为 useEffect 依赖以重新触发动画。
 *
 * @param state  - current DiagnosticPanelContext / 当前上下文
 * @param event  - dispatched DiagnosticPanelEvent / 派发的事件
 * @returns next DiagnosticPanelContext / 下一上下文
 */
export function diagnosticPanelReducer(
  state: DiagnosticPanelContext,
  event: DiagnosticPanelEvent,
): DiagnosticPanelContext {
  switch (event.type) {
    // ----- Show -----
    case 'SHOW': {
      if (state.state !== DiagnosticPanelState.Hidden) return state;
      return {
        ...state,
        state: DiagnosticPanelState.Appearing,
        currentTurnIndex: event.turnIndex,
        animationStartTime: Date.now(),
      };
    }

    // ----- Appear animation end -----
    case 'APPEAR_ANIMATION_END': {
      if (state.state !== DiagnosticPanelState.Appearing) return state;
      return {
        ...state,
        state: DiagnosticPanelState.Visible,
        animationStartTime: null,
      };
    }

    // ----- Update turn -----
    case 'UPDATE_TURN': {
      if (state.state !== DiagnosticPanelState.Visible) return state;
      // Save the previous turn index and set the new one immediately. The
      // animation effect will visually mask the swap via a cross-fade.
      // 保存上一弯序号并立即设置新序号。动画通过交叉淡入淡出遮盖切换。
      return {
        ...state,
        state: DiagnosticPanelState.Updating,
        prevTurnIndex: state.currentTurnIndex,
        currentTurnIndex: event.turnIndex,
        animationStartTime: Date.now(),
      };
    }

    // ----- Update animation end -----
    case 'UPDATE_ANIMATION_END': {
      if (state.state !== DiagnosticPanelState.Updating) return state;
      return {
        ...state,
        state: DiagnosticPanelState.Visible,
        animationStartTime: null,
        prevTurnIndex: null,
      };
    }

    // ----- Hide -----
    case 'HIDE': {
      // Valid from Visible or Updating.
      // 仅 Visible 或 Updating 可隐藏。
      if (
        state.state !== DiagnosticPanelState.Visible &&
        state.state !== DiagnosticPanelState.Updating
      ) {
        return state;
      }
      return {
        ...state,
        state: DiagnosticPanelState.Disappearing,
        animationStartTime: Date.now(),
      };
    }

    // ----- Disappear animation end -----
    case 'DISAPPEAR_ANIMATION_END': {
      if (state.state !== DiagnosticPanelState.Disappearing) return state;
      return {
        ...state,
        state: DiagnosticPanelState.Hidden,
        currentTurnIndex: null,
        animationStartTime: null,
        prevTurnIndex: null,
      };
    }

    default:
      return state;
  }
}

// ===== Public API =====

/**
 * Public API surface returned by {@link useDiagnosticPanel}.
 */
export interface DiagnosticPanelApi {
  /** Current diagnostic panel context. / 当前诊断面板上下文。 */
  state: DiagnosticPanelContext;
  /**
   * SHOW — from Hidden, start the appear animation and set currentTurnIndex.
   * / 从 Hidden 开始出现动画并设置当前弯序号。
   */
  show: (turnIndex: number) => void;
  /**
   * HIDE — from Visible or Updating, start the disappear animation.
   * / 从 Visible 或 Updating 开始消失动画。
   */
  hide: () => void;
  /**
   * UPDATE_TURN — from Visible, start the cross-fade animation and update
   * currentTurnIndex. / 从 Visible 开始交叉淡入淡出动画并更新弯序号。
   */
  updateTurn: (turnIndex: number) => void;
  /**
   * Ref to attach to the panel DOM element. The animation effects read this
   * ref to call `element.animate()`.
   *
   * 附加到面板 DOM 元素的 ref。动画 effect 通过此 ref 调用 element.animate()。
   */
  panelRef: RefObject<HTMLDivElement>;
}

// ===== Hook =====

/**
 * SM-4 diagnostic panel state machine hook.
 *
 * Owns the reducer and manages three Web Animations API side effects (one per
 * animation state). Each effect runs `element.animate()` on the panel DOM
 * element (via `panelRef`) and dispatches the corresponding `*_ANIMATION_END`
 * event on finish. If the element is not mounted, the end event is dispatched
 * immediately so the state machine never stalls.
 *
 * 拥有 reducer 并管理三个 Web Animations API 副作用（每个动画状态一个）。
 * 每个 effect 在面板 DOM 元素（通过 panelRef）上调用 element.animate()，完成时
 * 派发对应的 *_ANIMATION_END 事件。若元素未挂载，立即派发结束事件以免状态机卡住。
 *
 * @returns the diagnostic panel API (see {@link DiagnosticPanelApi})
 */
export function useDiagnosticPanel(): DiagnosticPanelApi {
  const [state, dispatch] = useReducer(diagnosticPanelReducer, initialState);

  // panelRef is attached to the panel DOM element by the consumer.
  // panelRef 由消费方附加到面板 DOM 元素。
  const panelRef = useRef<HTMLDivElement>(null);

  // --- Appearing animation ---
  // 出现动画
  // opacity 0→1, translateY 8px→0px, 200ms, ease-out.
  useEffect(() => {
    if (state.state !== DiagnosticPanelState.Appearing) return;
    const el = panelRef.current;
    if (!el) {
      // Element not mounted — skip animation, advance state immediately.
      // 元素未挂载 — 跳过动画，立即推进状态。
      dispatch({ type: 'APPEAR_ANIMATION_END' });
      return;
    }
    const anim = el.animate(
      [
        { opacity: 0, transform: `translateY(${TRANSLATE_OFFSET_PX}px)` },
        { opacity: 1, transform: 'translateY(0px)' },
      ],
      { duration: APPEAR_DURATION_MS, easing: 'ease-out' },
    );
    anim.onfinish = () => dispatch({ type: 'APPEAR_ANIMATION_END' });
    return () => anim.cancel();
  }, [state.state, state.animationStartTime]);

  // --- Updating animation (cross-fade) ---
  // 更新动画（交叉淡入淡出）
  // Two-phase: fade out (opacity 1→0, 150ms) → fade in (opacity 0→1, 150ms).
  // The content swap happened in the reducer (currentTurnIndex updated); the
  // fade-out phase masks it, and the fade-in phase reveals the new content.
  //
  // 两阶段：淡出（opacity 1→0, 150ms）→ 淡入（opacity 0→1, 150ms）。
  // 内容切换已在 reducer 完成（currentTurnIndex 已更新）；淡出阶段遮盖切换，
  // 淡入阶段显示新内容。
  useEffect(() => {
    if (state.state !== DiagnosticPanelState.Updating) return;
    const el = panelRef.current;
    if (!el) {
      dispatch({ type: 'UPDATE_ANIMATION_END' });
      return;
    }

    let cancelled = false;
    let fadeIn: Animation | null = null;

    // Phase 1: fade out (exit → ease-in).
    // 阶段1：淡出（退出 → ease-in）。
    const fadeOut = el.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: UPDATE_FADE_OUT_MS,
      easing: 'ease-in',
      fill: 'forwards',
    });

    fadeOut.onfinish = () => {
      if (cancelled) return;
      // Phase 2: fade in — new content becomes visible (enter → ease-out).
      // 阶段2：淡入 — 新内容可见（进入 → ease-out）。
      fadeIn = el.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: UPDATE_FADE_IN_MS,
        easing: 'ease-out',
        fill: 'forwards',
      });
      fadeIn.onfinish = () => {
        if (cancelled) return;
        dispatch({ type: 'UPDATE_ANIMATION_END' });
      };
    };

    return () => {
      cancelled = true;
      fadeOut.cancel();
      if (fadeIn) fadeIn.cancel();
    };
  }, [state.state, state.animationStartTime]);

  // --- Disappearing animation ---
  // 消失动画
  // opacity 1→0, translateY 0px→8px, 200ms, ease-in.
  useEffect(() => {
    if (state.state !== DiagnosticPanelState.Disappearing) return;
    const el = panelRef.current;
    if (!el) {
      dispatch({ type: 'DISAPPEAR_ANIMATION_END' });
      return;
    }
    const anim = el.animate(
      [
        { opacity: 1, transform: 'translateY(0px)' },
        { opacity: 0, transform: `translateY(${TRANSLATE_OFFSET_PX}px)` },
      ],
      { duration: DISAPPEAR_DURATION_MS, easing: 'ease-in' },
    );
    anim.onfinish = () => dispatch({ type: 'DISAPPEAR_ANIMATION_END' });
    return () => anim.cancel();
  }, [state.state, state.animationStartTime]);

  // --- Convenience methods ---
  // 便捷方法
  const show = useCallback((turnIndex: number) => {
    dispatch({ type: 'SHOW', turnIndex });
  }, []);
  const hide = useCallback(() => {
    dispatch({ type: 'HIDE' });
  }, []);
  const updateTurn = useCallback((turnIndex: number) => {
    dispatch({ type: 'UPDATE_TURN', turnIndex });
  }, []);

  // Assemble the API object. Memoized so consumers don't get a new object
  // identity on every render unless `state` actually changed. `panelRef` is
  // a stable ref and is intentionally omitted from the dependency array.
  // 组装 API 对象。使用 useMemo 防止每次渲染都产生新对象引用。
  // panelRef 是稳定 ref，故从依赖数组中省略。
  const api = useMemo<DiagnosticPanelApi>(
    () => ({
      state,
      show,
      hide,
      updateTurn,
      panelRef,
    }),
    [state, show, hide, updateTurn],
  );

  return api;
}
